import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const TYPE_LABEL = {
  equity_research: "Equity research",
  private_credit_memo: "Private credit",
  pe_investment_memo: "PE / VC memo",
  quarterly_portfolio_update: "Quarterly portfolio update",
};

const statusColor = (s) => {
  if (s === "completed") return "text-[var(--ink)]";
  if (s === "failed") return "text-[#a33]";
  return "text-[var(--saffron)]";
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/reports");
    setReports(r.data);
  };
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);

  const filtered = reports.filter((r) => filter === "all" || r.report_type === filter);

  return (
    <div className="max-w-[1100px] mx-auto px-10 py-12" data-testid="reports-page">
      <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)] border-b border-[var(--ink)]/15 pb-3">
        <span>Workspace · Library</span>
        <span>cosalpha</span>
      </div>

      <div className="mt-12 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="smallcaps text-[var(--saffron)]">No. 02 — Library</div>
          <h1 className="font-serif text-[48px] font-medium tracking-[-0.02em] mt-4">Reports.</h1>
        </div>
        <Link to="/app/reports/new" className="inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] px-6 py-3 text-[13px] tracking-wide transition-colors" data-testid="reports-new-button">
          <Plus className="w-3.5 h-3.5" /> New report
        </Link>
      </div>

      <div className="mt-10 flex items-center gap-1 text-[12px] border-b border-[var(--ink)]/15">
        {[
          { v: "all", l: "All" },
          { v: "quarterly_portfolio_update", l: "Quarterly" },
          { v: "pe_investment_memo", l: "PE / VC" },
          { v: "private_credit_memo", l: "Private credit" },
          { v: "equity_research", l: "Equity" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            className={`px-4 py-2.5 tracking-wide transition-colors border-b-2 -mb-px ${
              filter === t.v ? "text-[var(--ink)] font-medium border-[var(--saffron)]" : "text-[var(--warm-grey)] border-transparent hover:text-[var(--ink)]"
            }`}
            data-testid={`reports-filter-${t.v}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-6 stagger">
        {filtered.length === 0 && (
          <div className="bg-white border border-[var(--ink)]/15 p-12 text-center" data-testid="reports-empty">
            <div className="font-serif text-[24px] font-medium">Nothing here yet</div>
            <p className="text-[13px] text-[var(--ink)]/70 mt-2">Create your first report to see it listed here.</p>
          </div>
        )}
        {filtered.map((r, idx) => {
          const StatusIcon = r.status === "completed" ? CheckCircle2 : r.status === "failed" ? XCircle : Loader2;
          return (
            <Link
              key={r.id}
              to={`/app/reports/${r.id}`}
              className="grid grid-cols-12 gap-4 py-5 border-b border-[var(--ink)]/10 hover:bg-white items-baseline transition-colors"
              data-testid={`reports-row-${r.id}`}
            >
              <div className="col-span-1 font-mono text-[10px] text-[var(--saffron)] tabular">{`0${idx + 1}`.slice(-2)}</div>
              <div className="col-span-5">
                <div className="font-serif text-[20px] font-medium">{r.company_name} {r.ticker && <span className="text-[var(--warm-grey)] font-mono text-[12px] ml-1">({r.ticker})</span>}</div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--warm-grey)] mt-1">{r.sector || "—"}</div>
              </div>
              <div className="col-span-3 text-[12px] text-[var(--warm-grey)] tracking-[0.1em] uppercase font-semibold">{TYPE_LABEL[r.report_type]}</div>
              <div className="col-span-1 text-[12px] text-[var(--warm-grey)] tabular">{new Date(r.created_at).toLocaleDateString("en-IN")}</div>
              <div className={`col-span-2 text-right text-[11px] tracking-[0.18em] uppercase font-semibold ${statusColor(r.status)} flex items-center justify-end gap-1.5`}>
                <StatusIcon className={`w-3.5 h-3.5 ${r.status === "generating" ? "animate-spin" : ""}`} />
                {r.status}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
